import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ThemeToggle } from './ThemeToggle'
import { NavLink } from './NavLink'
import { LanguageToggle } from './LanguageToggle'
import { localeFromPath } from '@/i18n/locale'
import { DEFAULT_OFFER, OFFER_EN } from '@/data/offers'
import { homeBase, isHomePath } from '@/lib/sections'
import { scrollToAnchor } from '@/lib/scrollToAnchor'

/**
 * NAV_ITEMS:
 *   - href starting with `#` → секция на главной. NavLink-helper
 *     сам решает: scroll on home vs navigate to /#anchor off-home.
 *   - href starting with `/` → отдельный роут (router Link).
 *
 * На /en (Стадия 2: главная переведена целиком) якорные пункты зеркалят
 * русские; пункты на ru-only страницы (гайды/клуб) скрыты, чтобы
 * не выкидывать англоязычного посетителя на русскую страницу.
 */
/**
 * «Продукт», не «Продукты»: продукт ровно один (мандат Тони — «давал тебе
 * задачу на один»), и множественное число в меню обещало выбор, которого
 * за ссылкой нет.
 */
/**
 * Порядок пунктов повторяет порядок блоков на странице: калькулейтор идёт
 * сразу под кейсами, значит и в меню он стоит перед методологией. Меню,
 * которое расходится с порядком прокрутки, читается как беспорядок — и
 * человек не понимает, куда он попадёт.
 */
const NAV_ITEMS_RU = [
  { href: '#tracks', label: 'Продукт' },
  { href: '#cases', label: 'Кейсы' },
  { href: '#calculator', label: 'ROI-калькулятор' },
  { href: '#methodology-stages', label: 'Методология' },
  { href: '#partners', label: 'Партнёрка' },
  { href: '/blog', label: 'Материалы' },
]

const NAV_ITEMS_EN = [
  { href: '/en/ai-implementation', label: 'How it works' },
  { href: '#tracks', label: 'Product' },
  { href: '#cases', label: 'Cases' },
  { href: '#calculator', label: 'ROI calculator' },
  { href: '#methodology-stages', label: 'Methodology' },
  { href: '#partners', label: 'Partners' },
]

export function Nav() {
  const [open, setOpen] = useState(false)
  const { pathname, search } = useLocation()
  const isHome = isHomePath(pathname)
  const isEn = localeFromPath(pathname) === 'en'
  const NAV_ITEMS = isEn ? NAV_ITEMS_EN : NAV_ITEMS_RU
  // Один текст кнопки на всех точках страницы (ТЗ 5.2): берём из оффера,
  // чтобы шапка не разошлась с Hero, формой и плавающей кнопкой.
  const ctaText = isEn ? OFFER_EN.ctaText : DEFAULT_OFFER.ctaText
  // На ноутбуках 1024–1279 px русская строка шапки с полным текстом кнопки
  // не помещается (нужно ~1100 px, есть 928), поэтому там кнопка короче.
  // Английский текст короче и влезает целиком.
  const ctaShort = isEn ? ctaText : 'Записаться →'
  const menuLabel = isEn ? 'Open menu' : 'Открыть меню'

  return (
    <header className="sticky top-0 z-40 bg-bg/85 backdrop-blur-md border-b border-divider">
      <div className="container-x flex items-center justify-between h-16">
        {/* Логотип ведёт на главную своей локали. На главной (в том числе
            открытой по адресу блока, /roi) — ещё и плавно поднимает наверх:
            адрес возвращается к «/», а не обрастает «#top». */}
        <Link
          to={{ pathname: homeBase(pathname), search: isHome ? search : '' }}
          state={isHome ? { scrolled: true } : undefined}
          onClick={() => {
            if (isHome) scrollToAnchor('top')
          }}
          className="font-semibold tracking-tight text-ink no-underline whitespace-nowrap"
        >
          DNAI Engineering
        </Link>

        {/* Меню — на любом ноутбуке и компьютере, с 1024 px. Уже 1024 шесть
            пунктов, кнопка, язык и тема в одну строку физически не
            помещаются: до 2026-09-25 меню включалось с 768 px и на
            планшетах ломалось — пункты в две строки, язык и тема за краем
            экрана. Там бургер и кнопка записи рядом. На 1024–1279 строка
            плотнее и кнопка короче, с 1280 — полный текст.
            Страж — tests/e2e/header-fit.spec.ts: меню видно с 1024 px и
            целиком помещается на всех ширинах. */}
        <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.href}
              href={item.href}
              className="text-[0.8125rem] xl:text-small text-muted no-underline hover:text-ink transition-colors whitespace-nowrap"
            >
              {item.label}
            </NavLink>
          ))}
          <NavLink
            href="#final-cta"
            className="btn-primary py-2 px-4 xl:px-5 text-small no-underline whitespace-nowrap"
          >
            {ctaShort === ctaText ? (
              ctaText
            ) : (
              <>
                <span className="xl:hidden">{ctaShort}</span>
                <span className="hidden xl:inline">{ctaText}</span>
              </>
            )}
          </NavLink>
          <LanguageToggle />
          <ThemeToggle />
        </nav>

        {/* Бургер — до 1024 px. На планшете рядом с ним стоит кнопка записи:
            плавающая кнопка внизу живёт только на телефоне, и без неё CTA
            на экранах 768–1023 px пропал бы из виду. */}
        <div className="lg:hidden flex items-center gap-3">
          <NavLink
            href="#final-cta"
            className="hidden md:inline-flex btn-primary py-2 px-4 text-small no-underline whitespace-nowrap"
          >
            {ctaText}
          </NavLink>
          <button
            type="button"
            aria-label={menuLabel}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="p-2 -mr-2"
          >
            <span className="block w-6 h-0.5 bg-ink mb-1.5" />
            <span className="block w-6 h-0.5 bg-ink mb-1.5" />
            <span className="block w-6 h-0.5 bg-ink" />
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-divider bg-bg">
          <div className="container-x py-6 flex flex-col gap-4">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="text-body text-ink no-underline py-1"
              >
                {item.label}
              </NavLink>
            ))}
            <NavLink
              href="#final-cta"
              onClick={() => setOpen(false)}
              className="btn-primary no-underline mt-2"
            >
              {ctaText}
            </NavLink>
            <div className="mt-2 pt-4 border-t border-divider flex items-center justify-between">
              <LanguageToggle compact />
              <ThemeToggle compact />
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
